import type { Seconds } from "@rosette/ts/src/util/units.js";
import { getTimeSeconds } from "@rosette/ts/src/util/utils.js";
import { clamp } from "@rosette/ts/src/util/numbers.js";
import { rand } from "@rosette/ts/src/util/random.js";


export class ParticleSystem {
    private readonly birthTime: Seconds[] = [];
    private readonly lifetime: Seconds[] = [];
    private readonly x: number[] = [];
    private readonly y: number[] = [];
    private readonly vx: number[] = [];
    private readonly vy: number[] = [];
    private readonly ax: number[] = [];
    private readonly ay: number[] = [];
    private readonly r: number[] = [];
    private readonly g: number[] = [];
    private readonly b: number[] = [];

    remove(index: number) {
        this.birthTime.splice(index, 1);
        this.lifetime.splice(index, 1);
        this.x.splice(index, 1);
        this.y.splice(index, 1);
        this.vx.splice(index, 1);
        this.vy.splice(index, 1);
        this.ax.splice(index, 1);
        this.ay.splice(index, 1);
        this.r.splice(index, 1);
        this.g.splice(index, 1);
        this.b.splice(index, 1);
    }

    removeDeadParticles() {
        const time = getTimeSeconds();

        let index = this.birthTime.length;
        while (index > 0) {
            index -= 1;

            const age = (time - this.birthTime[index]) / this.lifetime[index];
            if (age >= 1) {
                this.remove(index);
            }
        }
    }

    spawn(
        lifetime: Seconds,
        x: number, y: number,
        vx: number, vy: number,
        ax: number, ay: number,
        r: number, g: number, b: number,
    ) {
        this.birthTime.push(getTimeSeconds());
        this.lifetime.push(lifetime);
        this.x.push(x);
        this.y.push(y);
        this.vx.push(vx);
        this.vy.push(vy);
        this.ax.push(ax);
        this.ay.push(ay);
        this.r.push(r);
        this.g.push(g);
        this.b.push(b);
    }

    spawnExplosion(
        particleCount: number,
        x: number, y: number,
        speed: number,
        lifetime: Seconds,
        r: number, g: number, b: number,
        sphere: boolean,
        gravity: number,
    ) {
        const dev = (value: number, vx: number, vy: number) => {
            return clamp(value + rand(-20, 20) + 10 - 0.25 * Math.sqrt(vx * vx + vy * vy), 0, 255);
        };
        for (let index = 0; index < particleCount; ++index) {
            const angle = (index + rand()) * 360 / particleCount;
            const vl = speed * (sphere ? Math.sqrt(rand()) : rand());
            const vx = Math.cos(angle) * vl;
            const vy = Math.sin(angle) * vl;

            this.spawn(
                lifetime * (0.6 * Math.random() + 0.7),
                x, y,
                vx, vy,
                0, gravity,
                dev(r, vx, vy), dev(g, vx, vy), dev(b, vx, vy),
            );
        }
    }

    forEach(
        accept: (age: number, x: number, y: number, r: number, g: number, b: number) => void,
    ) {
        const time = getTimeSeconds();

        for (let index = 0; index < this.birthTime.length; ++index) {
            const ageSecs = (time - this.birthTime[index]);
            const age = ageSecs / this.lifetime[index];
            if (age > 1)
                continue;

            const x = this.x[index] + this.vx[index] * ageSecs + 0.5 * this.ax[index] * ageSecs * ageSecs;
            const y = this.y[index] + this.vy[index] * ageSecs + 0.5 * this.ay[index] * ageSecs * ageSecs;
            const r = this.r[index];
            const g = this.g[index];
            const b = this.b[index];
            accept(age, x, y, r, g, b);
        }
        this.removeDeadParticles();
    }
}
